import { useTranslation } from 'react-i18next'
import { connect, useDataItem } from 'scrivito'

import { ActivityTab } from '../SharedTabs/ActivityTab/ActivityTab'
import { AddressTab } from '../SharedTabs/AddressTab/AddressTab'
import { ContactTab } from '../SharedTabs/ContactTab/ContactTab'
import { DocumentTab } from '../SharedTabs/DocumentTab/DocumentTab'
import { InvoiceTab } from '../SharedTabs/InvoiceTab/InvoiceTab'
import { OpportunityTab } from '../SharedTabs/OpportunityTab/OpportunityTab'
import { OrderTab } from '../SharedTabs/OrderTab/OrderTab'
import { QuoteTab } from '../SharedTabs/QuoteTab/QuoteTab'
import { TicketTab } from '../SharedTabs/TicketTab/TicketTab'
import { NavTabs, NavTabItem } from '@/Atomic/Templates/NavTabs'
import { NavTab } from '@/Atomic/Widgets/DataItemTabs/const'
import { ActivityContactRelation } from '@/Data/ActivityContactRelation/ActivityContactRelationDataClass'
import { AddressContactRelation } from '@/Data/AddressContactRelation/AddressContactRelationDataClass'
import { DataClassName } from '@/Data/const'
import { ContactContactRelation } from '@/Data/ContactContactRelation/ContactContactRelationDataClass'
import { ContactDocumentRelation } from '@/Data/ContactDocumentRelation/ContactDocumentRelationDataClass'
import { ContactProjectRelation } from '@/Data/ContactProjectRelation/ContactProjectRelationDataClass'
import { Invoice } from '@/Data/Invoice/InvoiceDataClass'
import { Opportunity } from '@/Data/Opportunity/OpportunityDataClass'
import { Order } from '@/Data/Order/OrderDataClass'
import { Quote } from '@/Data/Quote/QuoteDataClass'
import { useFeatureFlagAccess } from '@/utils/featureFlagConfig'

const ALL_NAV_TABS: NavTab[] = [
  NavTab.ACTIVITY,
  NavTab.OPPORTUNITY,
  NavTab.QUOTE,
  NavTab.ORDER,
  NavTab.INVOICE,
  NavTab.ADDRESS,
  NavTab.CONTACT,
  NavTab.TICKET,
  NavTab.DOCUMENT,
]

export const CompanyDataItemTabs: React.FC = connect(() => {
  const { t } = useTranslation()
  const { filterTabs } = useFeatureFlagAccess()
  const dataItem = useDataItem()
  const dataItemId = dataItem?.id() ?? ''

  if (!dataItemId) return

  const visibleNavTabs = filterTabs(ALL_NAV_TABS)
  const isVisible = (tab: NavTab) => visibleNavTabs.includes(tab)

  const activityCount = isVisible(NavTab.ACTIVITY)
    ? (ActivityContactRelation.all()
        .transform({ filters: { contact: dataItemId } })
        .count() ?? 0)
    : 0

  const opportunityCount = isVisible(NavTab.OPPORTUNITY)
    ? (Opportunity.all()
        .transform({ filters: { customer: dataItemId } })
        .count() ?? 0)
    : 0

  const quoteCount = isVisible(NavTab.QUOTE)
    ? (Quote.all()
        .transform({ filters: { customer: dataItemId } })
        .count() ?? 0)
    : 0

  const orderCount = isVisible(NavTab.ORDER)
    ? (Order.all()
        .transform({ filters: { customer: dataItemId } })
        .count() ?? 0)
    : 0

  const invoiceCount = isVisible(NavTab.INVOICE)
    ? (Invoice.all()
        .transform({ filters: { invoiceRecipient: dataItemId } })
        .count() ?? 0)
    : 0

  const addressCount = isVisible(NavTab.ADDRESS)
    ? (AddressContactRelation.all()
        .transform({ filters: { contact: dataItemId } })
        .count() ?? 0)
    : 0

  const contactCount = isVisible(NavTab.CONTACT)
    ? (ContactContactRelation.all()
        .transform({
          filters: {
            father: dataItemId,
            contactEntity: 'Person',
          },
        })
        .count() ?? 0)
    : 0

  const ticketCount = isVisible(NavTab.TICKET)
    ? (ContactProjectRelation.all()
        .transform({
          filters: {
            contact: dataItemId,
            projectEntity: 'Ticket',
          },
        })
        .count() ?? 0)
    : 0

  const documentCount = isVisible(NavTab.DOCUMENT)
    ? (ContactDocumentRelation.all()
        .transform({ filters: { contact: dataItemId } })
        .count() ?? 0)
    : 0

  const tabsByNavTab: Array<{ navTab: NavTab; tab: NavTabItem }> = [
    {
      navTab: NavTab.ACTIVITY,
      tab: {
        id: 'activities',
        title: t('pages.activities.title'),
        icon: 'activity',
        count: activityCount,
        content: <ActivityTab searchInputName="search company-activity" />,
      },
    },
    {
      navTab: NavTab.OPPORTUNITY,
      tab: {
        id: 'opportunities',
        title: t('components.breadcrumbs.opportunities'),
        icon: 'fire',
        count: opportunityCount,
        content: (
          <OpportunityTab searchInputName="search company-opportunity" />
        ),
      },
    },
    {
      navTab: NavTab.QUOTE,
      tab: {
        id: 'quotes',
        title: t('components.breadcrumbs.quotes'),
        icon: 'invoice',
        count: quoteCount,
        content: <QuoteTab searchInputName="search company-quote" />,
      },
    },
    {
      navTab: NavTab.ORDER,
      tab: {
        id: 'orders',
        title: t('components.breadcrumbs.orders'),
        icon: 'file-check',
        count: orderCount,
        content: <OrderTab searchInputName="search company-order" />,
      },
    },
    {
      navTab: NavTab.INVOICE,
      tab: {
        id: 'invoices',
        title: t('components.invoice.title'),
        icon: 'invoice',
        count: invoiceCount,
        content: (
          <InvoiceTab
            searchInputName="search company-invoice"
            subjectRelation="invoiceRecipient"
          />
        ),
      },
    },
    {
      navTab: NavTab.ADDRESS,
      tab: {
        id: 'addresses',
        title: t('components.address.addresses'),
        icon: 'pin',
        count: addressCount,
        content: <AddressTab searchInputName="company-address-tab-search" />,
      },
    },
    {
      navTab: NavTab.CONTACT,
      tab: {
        id: 'contacts',
        title: t('components.contact.title'),
        icon: 'user-multiple',
        count: contactCount,
        content: <ContactTab searchInputName="search company-contact" />,
      },
    },
    {
      navTab: NavTab.TICKET,
      tab: {
        id: 'tickets',
        title: t('components.breadcrumbs.tickets'),
        icon: 'ticket',
        count: ticketCount,
        content: <TicketTab searchInputName="search company-ticket" />,
      },
    },
    {
      navTab: NavTab.DOCUMENT,
      tab: {
        id: 'documents',
        title: t('components.document.documents'),
        icon: 'file-empty',
        count: documentCount,
        content: (
          <DocumentTab relationTo={DataClassName.CONTACT_DOCUMENT_RELATION} />
        ),
      },
    },
  ]

  const tabs: NavTabItem[] = tabsByNavTab
    .filter((entry) => isVisible(entry.navTab))
    .map((entry) => entry.tab)

  if (!tabs.length) return

  return <NavTabs tabs={tabs} />
})
